import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { FormFieldDto } from "../../general/dtos";
import { createFormFieldDom, RENDERER_SCOPED_STYLES } from '../content-viewer/rendering-helpers';

interface Props {
    visible: boolean;
    initialAttributes: any; 
    previewData: Partial<FormFieldDto>; 
    onSave: (updatedAttributes: any) => void; 
    onCancel: () => void;
}

function PreviewRenderer({ field, attributes }: { field: Partial<FormFieldDto>, attributes: any }) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.innerHTML = "";
            const domElement = createFormFieldDom({ ...field, attributes } as FormFieldDto);
            
            domElement.querySelectorAll('input, select, textarea').forEach(el => 
                el.setAttribute('disabled', 'true')
            );
            containerRef.current.appendChild(domElement);
        }
    }, [field, attributes]);

    return (
        <div 
            ref={containerRef} 
            className="p-3 border-round border-1 surface-border" 
            style={{ minHeight: '80px', width: '100%' }}
        />
    );
}

export const ManageAttributesDialog: React.FC<Props> = ({
    visible,
    initialAttributes,
    previewData,
    onSave,
    onCancel
}) => {
    // Standardize the shape of attributes immediately to prevent "not iterable" errors
    const getSafeAttributes = (attrs: any) => ({
        container: attrs?.container || [],
        label: attrs?.label || [],
        input: attrs?.input || []
    });

    const [localAttrs, setLocalAttrs] = useState(getSafeAttributes(initialAttributes));

    useEffect(() => {
        if (visible) {
            setLocalAttrs(getSafeAttributes(initialAttributes));
        }
    }, [visible, initialAttributes]);

    const handleAttributeChange = (category: 'container' | 'label' | 'input', index: number, key: string, value: string) => {
        setLocalAttrs(prev => {
            // Safety: Ensure we are spreading an array, even if state was somehow corrupted
            const currentCategory = prev[category] || [];
            const newCat = [...currentCategory];
            newCat[index] = { [key]: value };
            return { ...prev, [category]: newCat }; 
        });
    };

    const addAttribute = (category: 'container' | 'label' | 'input') => {
        setLocalAttrs(prev => ({
            ...prev,
            [category]: [...(prev[category] || []), { "": "" }]
        }));
    };

    const removeAttribute = (category: 'container' | 'label' | 'input', index: number) => {
        setLocalAttrs(prev => ({
            ...prev,
            [category]: (prev[category] || []).filter((_: any, i: number) => i !== index)
        }));
    };

    const renderAttributeEditor = (category: 'container' | 'label' | 'input') => (
        <div className="flex flex-column gap-3 p-2">
            {(localAttrs[category] || []).map((attr: any, index: number) => {
                const entries = Object.entries(attr || {})[0] || ["", ""];
                const key = entries[0] as string;
                const value = entries[1] as string;
                return (
                    <div key={index} className="flex align-items-center gap-2">
                        <InputText value={key} 
                            onChange={(e) => handleAttributeChange(category, index, e.target.value, value)} 
                            placeholder="Key" className="flex-1" />
                        <InputText value={value} 
                            onChange={(e) => handleAttributeChange(category, index, key, e.target.value)} 
                            placeholder="Value" className="flex-1" />
                        <Button 
                            icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => removeAttribute(category, index)} 
                        />
                    </div>
                );
            })}
            <Button label="Add attribute" icon="pi pi-plus" className="p-button-outlined p-button-sm w-max" onClick={() => addAttribute(category)} />
        </div>
    );

    return (
        <Dialog 
            header="Field attributes & styles" 
            visible={visible} 
            style={{ width: '90vw', height: '90vh' }}
            contentStyle={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            onHide={onCancel}
            footer={
                <div className="flex justify-content-end gap-2 p-2">
                    <Button label="Cancel" onClick={onCancel} className="p-button-text" />
                    <Button label="Apply" icon="pi pi-check" onClick={() => onSave(localAttrs)} />
                </div>
            }
        >
            <style>{RENDERER_SCOPED_STYLES}</style>

            <div className="flex-grow-1 overflow-auto p-2">
                <TabView>
                    <TabPanel header="Container">{renderAttributeEditor('container')}</TabPanel>
                    <TabPanel header="Label">{renderAttributeEditor('label')}</TabPanel>
                    <TabPanel header="Input">{renderAttributeEditor('input')}</TabPanel>
                </TabView>
            </div>

            <div className="p-3 border-top-1 surface-border mt-auto">
                <span className="text-xs font-bold text-500 uppercase block mb-2">Runtime preview</span>
                <PreviewRenderer field={previewData} attributes={localAttrs} />
            </div>
        </Dialog>
    );
};